import { useEffect } from 'react';

/**
 * StorefrontSEO (v4.25) — client-side SEO head manager for the unified
 * meal-pass storefront and its dedicated About/Contact pages.
 *
 * Renders NOTHING. In an effect it:
 *   1. Sets document.title (brand.seo_title override → per-page default).
 *   2. Upserts <meta name="description"> (brand.seo_description → per-page
 *      default built from brand + catalog).
 *   3. Upserts Open Graph + Twitter card tags.
 *   4. Injects a single JSON-LD @graph <script> (Restaurant + Product/Offer
 *      nodes + optional FAQPage + BreadcrumbList).
 *
 * Every element THIS component creates is tagged data-storefront-seo="1" and
 * removed on cleanup, so navigating between storefront pages never accumulates
 * stale/duplicate tags. Tags that already exist (e.g. the description meta from
 * public/index.html) are reused in place — found-or-created — and their content
 * is simply overwritten, never duplicated.
 *
 * IMPORTANT (repo house rule): no `=== 1` / `=== 0` strict compares against
 * boolean-ish JSON. DOM writes are idempotent (find-or-create by name/property).
 *
 * CRA has no SSR — Google renders JS and sees these tags, but social
 * link-unfurl scrapers (Facebook/Slack/Twitter/iMessage) do NOT execute JS and
 * will only ever see public/index.html's static tags. Full social-unfurl SEO
 * needs prerendering/SSR. The server-rendered /api/storefront/{subdomain}/seo.json
 * feed partially mitigates for AI agents/crawlers that don't run JS.
 */

const SEO_ATTR = 'data-storefront-seo';

// ── Price helpers ──────────────────────────────────────────────────────────
const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : null);

const planFromPrice = (plan) => {
  const tiers = Array.isArray(plan?.pricing?.tiers) ? plan.pricing.tiers : [];
  const prices = tiers
    .map((t) => num(t?.price))
    .filter((p) => p !== null && p > 0);
  if (prices.length) return Math.min(...prices);
  return num(plan?.base_price) ?? num(plan?.cost_per_package);
};

const cateringFromPrice = (pkg) =>
  num(pkg?.price_per_person) ?? num(pkg?.base_price) ?? null;

const isPrimaryPlan = (p) =>
  p && p.id !== 'system-custom-plan' && !p.is_meal_addon && !p.is_delivery_addon && p.is_active !== false;

// ── Per-page copy ───────────────────────────────────────────────────────────
const PAGE_SUFFIX = {
  home: null, // home uses the tagline
  about: 'About',
  contact: 'Contact',
  catering: 'Catering',
  passes: 'Meal Passes',
  plans: 'Meal Plans',
};

const SCHEMA_DAYS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const HHMM = /^\d{1,2}:\d{2}$/;

/** schema.org OpeningHoursSpecification rows from the store's own weekly hours;
 *  days marked closed, absent, or with an unreadable time emit nothing. */
export const openingHoursSpecification = (operatingHours) => {
  if (!operatingHours || typeof operatingHours !== 'object') return [];
  return Object.keys(SCHEMA_DAYS).flatMap((d) => {
    const h = operatingHours[d];
    if (!h || h.closed === true || h.is_closed === true) return [];
    const opens = String(h.open || '').trim();
    const closes = String(h.close || '').trim();
    if (!HHMM.test(opens) || !HHMM.test(closes)) return [];
    return [{ '@type': 'OpeningHoursSpecification', dayOfWeek: SCHEMA_DAYS[d], opens, closes }];
  });
};

const StorefrontSEO = ({
  brand,
  subdomain,
  pageType = 'home',
  programs = [],
  mealPlans = [],
  cateringPackages = [],
  faqs = [],
  // v6 — the public settings feed (operating hours, pickup address); optional,
  // the pre-v6 pages do not pass it and emit no hours.
  settings = null,
  // v4.25.1 — generated-but-unclaimed launch-engine previews must be blocked
  // from search indexing (safe-marketing §8.3). When true, a robots noindex
  // meta is written and NO JSON-LD is injected (a concept preview must not
  // enter structured-data channels either).
  isConceptPreview = false,
}) => {
  useEffect(() => {
    if (!brand) return undefined;

    const head = document.head;
    const created = []; // elements this render appended (removed on cleanup)

    // Concept previews: noindex + no structured data, title still branded.
    if (isConceptPreview) {
      const robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex, nofollow');
      robots.setAttribute('data-storefront-seo', '1');
      head.appendChild(robots);
      created.push(robots);
      document.title = `${brand.brand_name || 'Restaurant'} — Private Concept Preview`;
      return () => { created.forEach((el) => el.remove()); };
    }
    const brandName = brand.brand_name || 'Meal Passes';
    // the SETTINGS feed is what the page prices in (v5.10: shown == charged); a pass
    // program's currency is only a fallback, and a plans-only store has no programs
    const currency = settings?.currency || programs?.[0]?.currency || 'USD';
    const url = (typeof window !== 'undefined' && window.location?.href) || '';
    const ogImage = brand.og_image_url || brand.logo_url || brand.hero_image_url || null;

    const primaryPlans = (mealPlans || []).filter(isPrimaryPlan);
    const passNames = (programs || []).map((p) => p.public_name || p.name).filter(Boolean);
    const planNames = primaryPlans.map((p) => p.alias || p.name).filter(Boolean);
    const cateringNames = (cateringPackages || []).map((c) => c.name).filter(Boolean);
    const effectiveFaqs = (Array.isArray(faqs) && faqs.length
      ? faqs
      : (Array.isArray(brand.faqs) ? brand.faqs : []))
      .filter((f) => f && f.q);

    // ── document.title ──────────────────────────────────────────────────
    let title = brand.seo_title;
    if (!title) {
      const suffix = pageType === 'home' ? brand.tagline : PAGE_SUFFIX[pageType];
      title = suffix ? `${brandName} — ${suffix}` : brandName;
    }
    document.title = title;

    // ── description ─────────────────────────────────────────────────────
    const list = (arr, max = 4) => arr.slice(0, max).join(', ');
    let description = brand.seo_description;
    if (!description) {
      const tagline = brand.tagline || brand.hero_subtitle || '';
      if (pageType === 'about') {
        description =
          brand.about_headline ||
          `The story behind ${brandName}.${tagline ? ` ${tagline}` : ''}`;
      } else if (pageType === 'contact') {
        const bits = [brand.contact_address, brand.contact_phone, brand.contact_email].filter(Boolean);
        description = `Get in touch with ${brandName}.${bits.length ? ` ${bits.join(' · ')}` : ''}`;
      } else if (pageType === 'catering') {
        description =
          brand.catering_subheadline ||
          `Catering from ${brandName}${cateringNames.length ? `: ${list(cateringNames)}` : ''}.`;
      } else if (pageType === 'passes') {
        description = `Prepaid meal passes at ${brandName}${passNames.length ? `: ${list(passNames)}` : ''}. Credits never expire.`;
      } else if (pageType === 'plans') {
        description = `Meal plan subscriptions from ${brandName}${planNames.length ? `: ${list(planNames)}` : ''}.`;
      } else {
        // home
        const offers = [...passNames, ...planNames, ...cateringNames];
        description = `${tagline || `Order from ${brandName}`}${offers.length ? ` — ${list(offers, 5)}.` : '.'}`;
      }
    }

    // ── find-or-create meta upsert ──────────────────────────────────────
    const upsertMeta = (keyType, key, content) => {
      if (!content) return;
      let el = head.querySelector(`meta[${keyType}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(keyType, key);
        el.setAttribute(SEO_ATTR, '1');
        head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('name', 'description', description);

    // Open Graph
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', brandName);
    if (url) upsertMeta('property', 'og:url', url);
    if (ogImage) upsertMeta('property', 'og:image', ogImage);

    // Twitter card
    upsertMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (ogImage) upsertMeta('name', 'twitter:image', ogImage);

    // ── JSON-LD @graph ──────────────────────────────────────────────────
    const graph = [];

    const sameAs = [brand.website_url, brand.instagram_url, brand.facebook_url].filter(Boolean);
    const restaurantId = `${url || brandName}#restaurant`;
    const restaurant = {
      '@type': 'Restaurant',
      '@id': restaurantId,
      name: brandName,
      description,
      url: url || undefined,
    };
    const restImage = ogImage || brand.logo_url;
    if (restImage) restaurant.image = restImage;
    if (brand.logo_url) restaurant.logo = brand.logo_url;
    if (brand.contact_phone) restaurant.telephone = brand.contact_phone;
    if (brand.contact_email) restaurant.email = brand.contact_email;
    if (brand.cuisine) restaurant.servesCuisine = brand.cuisine;
    if (brand.contact_address) {
      restaurant.address = { '@type': 'PostalAddress', streetAddress: brand.contact_address };
    } else if (settings?.pickup_address) {
      restaurant.address = { '@type': 'PostalAddress', streetAddress: settings.pickup_address };
    }
    // Opening hours ONLY from the merchant's own store-hours setting (never invented):
    // `operating_hours` is keyed mon..sun with {open, close, closed}.
    const hours = settings?.store_hours_enabled ? openingHoursSpecification(settings.operating_hours) : [];
    if (hours.length) restaurant.openingHoursSpecification = hours;
    if (sameAs.length) restaurant.sameAs = sameAs;
    graph.push(restaurant);

    // Product/Offer nodes — included on home + the passes/catering/plans pages.
    const wantOffers = pageType === 'home' || pageType === 'passes' || pageType === 'catering' || pageType === 'plans';
    if (wantOffers) {
      const pushProduct = (name, desc, price, curr, extra) => {
        if (!name) return;
        const node = {
          '@type': 'Product',
          name,
          brand: { '@type': 'Brand', name: brandName },
          ...(extra || {}),
        };
        if (desc) node.description = desc;
        const p = num(price);
        if (p !== null && p > 0) {
          node.offers = {
            '@type': 'Offer',
            price: p,
            priceCurrency: curr || currency,
            availability: 'https://schema.org/InStock',
            ...(url ? { url } : {}),
          };
        }
        graph.push(node);
      };

      if (pageType === 'home' || pageType === 'passes') {
        (programs || []).forEach((p) =>
          pushProduct(p.public_name || p.name, p.description, p.price, p.currency, { category: 'MealPass' })
        );
      }
      if (pageType === 'home' || pageType === 'plans') {
        primaryPlans.forEach((p) =>
          pushProduct(p.alias || p.name, p.description, planFromPrice(p), currency, { category: 'MealPlan' })
        );
      }
      if (pageType === 'home' || pageType === 'catering') {
        (cateringPackages || []).forEach((c) =>
          pushProduct(c.name, c.description, cateringFromPrice(c), currency, { category: 'Catering' })
        );
      }
    }

    // FAQPage
    if (effectiveFaqs.length) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: effectiveFaqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a || '' },
        })),
      });
    }

    // BreadcrumbList
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const storeBase = `${origin}/customers`;
    const crumbs = [{ name: brandName, item: storeBase }];
    if (pageType !== 'home') {
      crumbs.push({ name: PAGE_SUFFIX[pageType] || pageType, item: url });
    }
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: c.item || undefined,
      })),
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SEO_ATTR, '1');
    // Escape "<" so a stray "</script>" in any field can't break out of the tag.
    script.text = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(/</g, '\\u003c');
    head.appendChild(script);
    created.push(script);

    return () => {
      created.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
    // v5.67 - `settings` is a separate fetch that lands after `brand`; it has
    // to be a dependency or the opening hours stay what the FIRST run saw.
  }, [brand, subdomain, pageType, programs, mealPlans, cateringPackages, faqs, settings]);

  return null;
};

export default StorefrontSEO;
